import { supabase } from '@/integrations/supabase/client';

export interface EdgeFunctionResult<T> {
  data: T | null;
  error: Error | null;
}

/**
 * Invokes a Supabase Edge Function with automatic error-context JSON extraction.
 * Relies on the Supabase client's native authentication session management (no manual bearer tokens).
 */
export async function invokeEdgeFunction<TData = unknown, TBody = Record<string, unknown>>(
  functionName: string,
  body?: TBody
): Promise<EdgeFunctionResult<TData>> {
  try {
    const { data, error } = await supabase.functions.invoke<TData>(functionName, {
      body,
    });

    if (error) {
      let message = error.message || `Failed to execute ${functionName}`;
      if ('context' in (error as any)) {
        try {
          const bodyJson = await (error as any).context.json();
          if (bodyJson?.error || bodyJson?.message) {
            message = bodyJson.error || bodyJson.message;
          }
        } catch {
          // ignore json parse error, fall back to error.message
        }
      }
      return { data: null, error: new Error(message) };
    }

    return { data: data ?? null, error: null };
  } catch (err: unknown) {
    const error = err instanceof Error ? err : new Error(String(err));
    return { data: null, error };
  }
}
